import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { ArrowLeft, DollarSign, TrendingUp, TrendingDown, Download, Eye } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import axios from 'axios';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '../../components/ui/select';

const Financials = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  
  const [selectedYear, setSelectedYear] = useState('2024');
  const [selectedQuarter, setSelectedQuarter] = useState('Q4');
  const [loading, setLoading] = useState(true);
  const [financialSummary, setFinancialSummary] = useState({
    total_revenue: 0,
    total_expenses: 0,
    net_balance: 0,
    by_category: {}
  });
  const [financialRecords, setFinancialRecords] = useState([]);

  useEffect(() => {
    fetchFinancialData();
  }, []);

  const fetchFinancialData = async () => {
    try {
      setLoading(true);
      
      const [summaryRes, recordsRes] = await Promise.all([
        axios.get(`${backendUrl}/api/executive/financials/summary`),
        axios.get(`${backendUrl}/api/executive/financials`)
      ]);
      
      setFinancialSummary(summaryRes.data);
      setFinancialRecords(recordsRes.data || []);
      
    } catch (error) {
      console.error('Error fetching financial data:', error);
      toast({
        title: 'Error',
        description: 'Failed to load financial data',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const reports = financialRecords.slice(0, 10);

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return 'R 0';
    return `R ${amount.toLocaleString()}`;
  };
  
  // Helper to safely get category amount
  const getCategoryAmount = (category) => {
    return financialSummary.by_category?.[category] || 0;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="bg-[#2c3e7d] text-white py-16">
        <div className="container mx-auto px-4">
          <Button
            variant="ghost"
            onClick={() => navigate('/executive')}
            className="text-white hover:text-white hover:bg-white/10 mb-4"
          >
            <ArrowLeft size={20} className="mr-2" />
            Back to Executive Members
          </Button>
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold uppercase tracking-wide">
              FINANCIALS
            </h1>
            <div className="w-24 h-1 bg-white mx-auto mt-4"></div>
            <p className="mt-4 text-lg text-white/90">
              Financial reports and statements for executive review
            </p>
          </div>
        </div>
      </div>
      
      <div className="container mx-auto px-4 py-12">
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#2c3e7d]"></div>
          </div>
        ) : (
          <>
            {/* Filters */}
            <div className="flex flex-col md:flex-row gap-4 mb-8 max-w-5xl mx-auto">
              <Select value={selectedYear} onValueChange={setSelectedYear}>
                <SelectTrigger className="w-full md:w-48">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="2024">2024</SelectItem>
                  <SelectItem value="2023">2023</SelectItem>
                  <SelectItem value="2022">2022</SelectItem>
                </SelectContent>
              </Select>
              
              <Select value={selectedQuarter} onValueChange={setSelectedQuarter}>
                <SelectTrigger className="w-full md:w-48">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Q1">Q1 - Jan to Mar</SelectItem>
                  <SelectItem value="Q2">Q2 - Apr to Jun</SelectItem>
                  <SelectItem value="Q3">Q3 - Jul to Sep</SelectItem>
                  <SelectItem value="Q4">Q4 - Oct to Dec</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Financial Summary Cards */}
            <div className="max-w-5xl mx-auto mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Financial Summary - {selectedYear} {selectedQuarter}</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white">
                  <CardContent className="p-6">
                    <div className="flex justify-between items-start mb-2">
                      <p className="text-white/80 text-sm">Total Revenue</p>
                      <TrendingUp size={24} />
                    </div>
                    <p className="text-3xl font-bold">{formatCurrency(financialSummary.total_revenue)}</p>
                    <p className="text-white/80 text-sm mt-2">From all revenue sources</p>
                  </CardContent>
                </Card>

                <Card className="bg-gradient-to-br from-red-500 to-red-600 text-white">
                  <CardContent className="p-6">
                    <div className="flex justify-between items-start mb-2">
                      <p className="text-white/80 text-sm">Total Expenses</p>
                      <TrendingDown size={24} />
                    </div>
                    <p className="text-3xl font-bold">{formatCurrency(financialSummary.total_expenses)}</p>
                    <p className="text-white/80 text-sm mt-2">All operational costs</p>
                  </CardContent>
                </Card>

                <Card className={`bg-gradient-to-br ${financialSummary.net_balance >= 0 ? 'from-blue-500 to-blue-600' : 'from-orange-500 to-orange-600'} text-white`}>
                  <CardContent className="p-6">
                    <div className="flex justify-between items-start mb-2">
                      <p className="text-white/80 text-sm">Net Balance</p>
                      <DollarSign size={24} />
                    </div>
                    <p className="text-3xl font-bold">{formatCurrency(financialSummary.net_balance)}</p>
                    <p className="text-white/80 text-sm mt-2">{financialSummary.net_balance >= 0 ? 'Positive balance' : 'Net deficit'}</p>
                  </CardContent>
                </Card>
              </div>
            </div>

        {/* Revenue Breakdown */}
        <div className="max-w-5xl mx-auto mb-8">
          <Card>
            <CardHeader>
              <CardTitle>Revenue Breakdown</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-gray-700">Membership Fees</span>
                  <span className="font-semibold">{formatCurrency(getCategoryAmount('membership_fees'))}</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3">
                  <div className="bg-[#2c3e7d] h-3 rounded-full" style={{ width: financialSummary.total_revenue > 0 ? `${(getCategoryAmount('membership_fees') / financialSummary.total_revenue * 100).toFixed(0)}%` : '0%' }}></div>
                </div>

                <div className="flex justify-between items-center">
                  <span className="text-gray-700">Event Revenue</span>
                  <span className="font-semibold">{formatCurrency(getCategoryAmount('events_income'))}</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3">
                  <div className="bg-green-500 h-3 rounded-full" style={{ width: financialSummary.total_revenue > 0 ? `${(getCategoryAmount('events_income') / financialSummary.total_revenue * 100).toFixed(0)}%` : '0%' }}></div>
                </div>

                <div className="flex justify-between items-center">
                  <span className="text-gray-700">Sponsorships</span>
                  <span className="font-semibold">{formatCurrency(getCategoryAmount('sponsorships'))}</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3">
                  <div className="bg-blue-500 h-3 rounded-full" style={{ width: financialSummary.total_revenue > 0 ? `${(getCategoryAmount('sponsorships') / financialSummary.total_revenue * 100).toFixed(0)}%` : '0%' }}></div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Financial Reports */}
        <div className="max-w-5xl mx-auto">
          <Card>
            <CardHeader>
              <CardTitle>Financial Reports & Documents</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {reports.length > 0 ? (
                  reports.map((record) => (
                    <div
                      key={record.id}
                      className="flex flex-col md:flex-row md:items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors gap-3"
                    >
                      <div className="flex-1">
                        <h4 className="font-semibold text-gray-900">{record.description || 'Financial Record'}</h4>
                        <div className="flex flex-wrap gap-3 text-xs text-gray-500 mt-1">
                          <span>{record.category.replace('_', ' ').toUpperCase()}</span>
                          <span>•</span>
                          <span>{record.period}</span>
                          <span>•</span>
                          <span className={record.amount >= 0 ? 'text-green-600' : 'text-red-600'}>
                            {formatCurrency(Math.abs(record.amount))}
                          </span>
                        </div>
                        {record.notes && (
                          <p className="text-xs text-gray-600 mt-2">{record.notes}</p>
                        )}
                      </div>
                      
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline" disabled>
                          <Eye size={16} className="mr-1" />
                          View
                        </Button>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <p>No financial records found</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Financials;